import {
  CheckboxVisibility,
  DetailsListLayoutMode,
  DetailsRow,
  IColumn,
  ICommandBarItemProps,
  IDetailsRowProps,
  SelectionMode,
  ShimmeredDetailsList,
  Text,
} from '@fluentui/react';
import { Link } from '@micro-frontend-react/employee-experience/lib/Link';
import { useEffect } from 'react';
import * as React from 'react';

import { IMachineLearningExperiment } from '../../../Common/MachineLearningModels/IMachineLearningExperiment';
import * as Styled from '../../../Common/Styles/portalPage.styled';
import { useMachineLearningExperiments } from '../../../Hooks/MachineLearning/useMachineLearningExperiments';
import { AddMachineLearningExperimentPanel } from './AddMachineLearningExperimentPanel';
import { MachineLearningExperimentWizard } from './MachineLearningExperimentWizard/MachineLearningExperimentWizard';

export function MachineLearningExperiments(): React.ReactElement {
  const [experiments, reloadExperiments] = useMachineLearningExperiments();
  const [isWizardOpen, setWizardOpen] = React.useState<boolean>(false);
  const [isOpen, setOpen] = React.useState<boolean>(false);
  const [selectedExperiment, setSelectedExperiment] = React.useState<IMachineLearningExperiment | null>(null);

  const commandBarItems: ICommandBarItemProps[] = [
    {
      key: 'addMachineLearningExperiment',
      text: 'Add machine learning project',
      iconProps: { iconName: 'Add' },
      onClick: () => {
        setWizardOpen(true);
        setSelectedExperiment(null);
        setOpen(false);
      },
      buttonStyles: {
        root: {
          margin: '0px 0px 0px 0px',
        },
      },
    },
  ];

  const onRenderItemColumn = (item: IMachineLearningExperiment, index: number, column: IColumn): React.ReactNode => {
    if (column.key == 'name') {
      return (
        <div>
          <SimpleButton
            title={`Open ${item.name}`}
            onClick={() => {
              setSelectedExperiment(item);
            }}
          >
            {item.name}
          </SimpleButton>
        </div>
      );
    }
    if (column.key == 'disabled') {
      return <Text>{item.disabled ? 'Disabled' : 'Enabled'}</Text>;
    }
    if (column.key == 'result') {
      return (
        <StyledLink
          title="Go to Machine Learning Experiments Results"
          to={`/machine-learning/experiments/${item.id}/results`}
          usageEvent={{
            feature: 'HRDP',
            subFeature: 'HRDP.MachineLearningExperiments',
          }}
        >
          View results
        </StyledLink>
      );
    }
    return <Text>{item[column.key as keyof IMachineLearningExperiment]}</Text>;
  };

  const onRenderRow = (props: IDetailsRowProps) => {
    return <DetailsRow aria-busy={true} {...props} />;
  };

  useEffect(() => {
    if (selectedExperiment === null) return;

    setOpen(true);
  }, [selectedExperiment]);

  return (
    <div>
      <title>Machine Learning</title>
      <Styled.PortalPageHeadingSection>
        <Styled.PortalPageTitleText>Machine learning projects</Styled.PortalPageTitleText>
      </Styled.PortalPageHeadingSection>
      <Styled.ButtonsTableSection>
        <Styled.PortalPageCommandBar items={commandBarItems} ariaLabel="Command Bar" />

        <ShimmeredDetailsList
          items={experiments || []}
          enableShimmer={false}
          columns={experimentTableColumns}
          onRenderItemColumn={onRenderItemColumn}
          onRenderRow={onRenderRow}
          selectionMode={SelectionMode.none}
          layoutMode={DetailsListLayoutMode.justified}
          checkboxVisibility={CheckboxVisibility.hidden}
          ariaLabelForShimmer="Data Owners Table is being fetched"
          ariaLabelForGrid="Data Owners Table"
        />
      </Styled.ButtonsTableSection>
      <MachineLearningExperimentWizard
        isOpen={isWizardOpen}
        dismiss={() => {
          setWizardOpen(false);
          reloadExperiments();
        }}
      />
      {isOpen && (
        <AddMachineLearningExperimentPanel
          isOpen={isOpen}
          dismissPanel={() => {
            setOpen(false);
            setSelectedExperiment(null);
            reloadExperiments();
          }}
          experiment={selectedExperiment}
        />
      )}
    </div>
  );
}

const experimentTableColumns: IColumn[] = [
  {
    key: 'name',
    name: 'Name',
    minWidth: 350,
    maxWidth: 350,
  },
  {
    key: 'type',
    name: 'Type',
    minWidth: 250,
    maxWidth: 250,
  },
  {
    key: 'disabled',
    name: 'Status',
    minWidth: 150,
    maxWidth: 150,
  },
  {
    key: 'description',
    name: 'Description',
    minWidth: 350,
  },
  {
    key: 'result',
    name: 'Result',
    minWidth: 150,
    maxWidth: 150,
  },
];

const StyledLink = styled(Link)`
  font-size: 14px;
  line-height: 20px;
`;

const SimpleButton = styled.button`
  font-size: 14px;
  line-height: 20px;
  background: none;
  border: none;
  color: rgb(0, 90, 158);
  font-family: 'Segoe UI', 'Segoe UI Web (West European)', 'Segoe UI', -apple-system, BlinkMacSystemFont, Roboto,
    'Helvetica Neue', sans-serif;

  &:hover {
    text-decoration: underline;
  }
`;
